import { ApiError } from "@/lib/api";
import type { AuthedProgressUploader, AuthedRequester } from "@/lib/brokerAuth";
import { formatFileSize } from "@/lib/brokerDocumentTypes";
import { formatShortDate } from "@/lib/listFormat";

// Loss runs on a commercial submission (BRK-S361, PMS-S748). Nav
// (2026-10-15): "We would also need to collect loss on the submission for wc,
// display a button on the header which can used by a broker to upload the loss
// runs … If loss run isn't uploaded or collected we should display an
// indicator in the header that loss runs is required".
//
// A loss run is the report a carrier issues of the insured's claims under one
// policy term. PMS keeps each as a document on the submission (type
// `loss_run`) and says where they stand on every submission it sends
// (`lossRuns`): on file, not available (a reason the broker recorded), or
// missing. `withCount` restates that rule only to answer at once after a list
// change PMS has just confirmed; the next read of the submission is PMS's own.

const BASE = "/api/broker/submissions";

/** The document type a loss run is filed as. */
export const LOSS_RUN = "loss_run";

export type LossRunsStatus = "provided" | "not_available" | "missing";

/** Every status: the runtime copy the contract test holds to the pin. */
export const LOSS_RUNS_STATUSES: readonly LossRunsStatus[] = [
  "provided",
  "not_available",
  "missing",
];

export type NoLossRunsReason = "new_business" | "other";

/** Every reason, likewise held to the pin. */
export const NO_LOSS_RUNS_REASONS: readonly NoLossRunsReason[] = [
  "new_business",
  "other",
];

/** What each reason reads as, in the panel and on the card. */
export const NO_LOSS_RUNS_REASON_LABELS: Record<NoLossRunsReason, string> = {
  new_business: "A new business",
  other: "Another reason",
};

/** The longest note PMS takes with a reason. */
export const NO_LOSS_RUNS_NOTE_MAX = 1000;

/** Why there are no loss runs, as a broker recorded it. */
export interface NoLossRuns {
  reason: NoLossRunsReason;
  note: string | null;
  recordedAt: string;
  recordedBy: { name: string } | null;
}

/** Where a submission's loss runs stand, as PMS says on every submission. */
export interface LossRuns {
  /** Whether the line asks for them: Workers' Comp, for now. */
  required: boolean;
  status: LossRunsStatus;
  /** How many are on file. */
  count: number;
  notAvailable: NoLossRuns | null;
}

/** A document on a submission, as PMS lists it (oldest first). */
export interface SubmissionDocument {
  documentId: string;
  type: string;
  filename: string;
  contentType: string;
  byteSize: number;
  uploadedAt: string;
  uploadedBy: { name: string } | null;
}

function pathOf(id: string, rest: string): string {
  return `${BASE}/${encodeURIComponent(id)}${rest}`;
}

/** GET …/documents?type=loss_run: the loss runs on file. The array is
 *  checked rather than trusted, so drift reads as a handled failure, never as
 *  "none on file" (NN #7). */
export async function listLossRuns(
  request: AuthedRequester,
  id: string,
): Promise<SubmissionDocument[]> {
  const body = await request<{ documents?: SubmissionDocument[] }>(
    pathOf(id, `/documents?type=${LOSS_RUN}`),
  );
  if (!Array.isArray(body.documents)) {
    throw new ApiError(
      200,
      "We couldn’t read this submission’s loss runs — try again.",
      body,
    );
  }
  return body.documents;
}

/** POST …/documents: one file a request, of one document type, so each
 *  file's progress, Stop and refusal are its own. A 201 with nothing in it is
 *  not read as attached (NN #7). A loss run is one; a reply to Signal's
 *  request attaches others (BRK-S464). */
export async function uploadSubmissionDocument(
  upload: AuthedProgressUploader,
  id: string,
  input: {
    file: File;
    type: string;
    onProgress?: (progress: { loaded: number; total: number }) => void;
    signal?: AbortSignal;
  },
): Promise<SubmissionDocument> {
  const form = new FormData();
  form.append("files", input.file);
  form.append("type", input.type);
  const body = await upload<{ documents?: SubmissionDocument[] }>(
    pathOf(id, "/documents"),
    form,
    { onProgress: input.onProgress, signal: input.signal },
  );
  const [document] = body.documents ?? [];
  if (!document) {
    throw new ApiError(
      201,
      "That file was accepted but nothing came back to show — try it again.",
    );
  }
  return document;
}

/** A loss run, uploaded as `uploadSubmissionDocument` does. */
export function uploadLossRun(
  upload: AuthedProgressUploader,
  id: string,
  input: {
    file: File;
    onProgress?: (progress: { loaded: number; total: number }) => void;
    signal?: AbortSignal;
  },
): Promise<SubmissionDocument> {
  return uploadSubmissionDocument(upload, id, { ...input, type: LOSS_RUN });
}

/** DELETE …/documents/{docId}: takes a loss run off the submission. */
export function removeLossRun(
  request: AuthedRequester,
  id: string,
  documentId: string,
): Promise<void> {
  return request<void>(
    pathOf(id, `/documents/${encodeURIComponent(documentId)}`),
    { method: "DELETE" },
  );
}

function lossRunsOf(body: { lossRuns?: LossRuns }): LossRuns {
  if (typeof body.lossRuns !== "object" || body.lossRuns === null) {
    throw new ApiError(
      200,
      "We couldn’t read where this submission’s loss runs stand — reload to check.",
      body,
    );
  }
  return body.lossRuns;
}

/** PUT …/loss-runs/not-available: records why there are none. A blank note
 *  is sent as none at all. */
export async function recordNoLossRuns(
  request: AuthedRequester,
  id: string,
  input: { reason: NoLossRunsReason; note: string | null },
): Promise<LossRuns> {
  const note = input.note?.trim() ?? "";
  return lossRunsOf(
    await request<{ lossRuns?: LossRuns }>(
      pathOf(id, "/loss-runs/not-available"),
      { method: "PUT", body: { reason: input.reason, note: note || null } },
    ),
  );
}

/** DELETE …/loss-runs/not-available: clears the reason. */
export async function clearNoLossRuns(
  request: AuthedRequester,
  id: string,
): Promise<LossRuns> {
  return lossRunsOf(
    await request<{ lossRuns?: LossRuns }>(
      pathOf(id, "/loss-runs/not-available"),
      { method: "DELETE" },
    ),
  );
}

/** Where loss runs stand once `count` are on file: provided while any are,
 *  else not available when a reason is recorded, else missing. */
export function withCount(lossRuns: LossRuns, count: number): LossRuns {
  return {
    ...lossRuns,
    count,
    status:
      count > 0
        ? "provided"
        : lossRuns.notAvailable
          ? "not_available"
          : "missing",
  };
}

/** Whether the header asks for loss runs: required, none on file and no
 *  reason recorded, on a submission that isn't bound (PMS-S708). */
export function needsLossRuns(lossRuns: LossRuns, locked: boolean): boolean {
  return !locked && lossRuns.required && lossRuns.status === "missing";
}

/** The key fact: where they stand, and whether it wants the broker's eye. */
export function lossRunsFact(lossRuns: LossRuns): {
  value: string;
  attention: boolean;
} {
  switch (lossRuns.status) {
    case "provided":
      return { value: `${lossRuns.count} on file`, attention: false };
    case "not_available":
      return { value: "Not available", attention: false };
    case "missing":
      return lossRuns.required
        ? { value: "Required, none yet", attention: true }
        : { value: "None", attention: false };
  }
}

/** The header button's name: what it opens, then where they stand
 *  ("Loss runs, 2 on file"). */
export function lossRunsButtonName(
  lossRuns: LossRuns,
  locked: boolean,
): string {
  if (lossRuns.status === "provided") {
    return `Loss runs, ${String(lossRuns.count)} on file`;
  }
  if (lossRuns.status === "not_available") return "Loss runs, not available";
  return needsLossRuns(lossRuns, locked)
    ? "Loss runs, none uploaded yet"
    : "Loss runs";
}

/** What the header button carries: an amber dot while the header asks for
 *  them, their count once they're on file, else nothing. */
export function lossRunsBadge(
  lossRuns: LossRuns,
  locked: boolean,
): "dot" | number | null {
  if (lossRuns.status === "provided") return lossRuns.count;
  return needsLossRuns(lossRuns, locked) ? "dot" : null;
}

/** A file's line under its name: "1.4 MB · Added 10/15/2026 by Ava Dawson". */
export function documentLine(document: SubmissionDocument): string {
  const by = document.uploadedBy ? ` by ${document.uploadedBy.name}` : "";
  return `${formatFileSize(document.byteSize)} · Added ${formatShortDate(document.uploadedAt)}${by}`;
}

/** The reason's checks before it's sent, as PMS makes them: a reason, a
 *  note for another reason, and a note of 1,000 characters at most. */
export function noLossRunsErrors(
  reason: NoLossRunsReason | null,
  note: string,
): { reason?: string; note?: string } {
  const errors: { reason?: string; note?: string } = {};
  const trimmed = note.trim();
  if (reason === null) errors.reason = "Choose why there are no loss runs.";
  if (trimmed.length > NO_LOSS_RUNS_NOTE_MAX) {
    errors.note = `Keep the note to ${NO_LOSS_RUNS_NOTE_MAX.toLocaleString("en-US")} characters or fewer.`;
  } else if (reason === "other" && trimmed === "") {
    errors.note = "Say why there are no loss runs.";
  }
  return errors;
}
